import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, ModalPortal, ModalOverlay, ModalClose, ModalTitle, ModalDescription } from '@klaro7/ui';
import * as DialogPrimitive from '@radix-ui/react-dialog';

import { useCookieConsent } from '@/features/cookie-consent';
import { useAuth } from '../hooks/useAuth';
import { useWelcomeBannerPublic } from '@/features/settings/hooks/useServicesSettings';
import { shouldShowBanner } from '../lib/welcomeBannerVisibility';

/* Welcome banner – pop-up immagine intera per visitatori non registrati.
   Config (immagine, attivo, frequenza) gestita dal pannello superadmin. */

const DISMISS_KEY = 'klaro7_welcome_banner_dismissed';   // data ISO (modalità giorni)
const SESSION_KEY = 'klaro7_welcome_banner_session';     // flag (modalità sessione)

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

export function WelcomeBanner() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const { data: banner } = useWelcomeBannerPublic();
  // Prima la scelta sui cookie, poi il popup: aperti insieme, il tap sul banner
  // cookie chiuderebbe il popup come "click fuori" consumando la visualizzazione.
  const { hasDecided } = useCookieConsent();

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isLoading || !banner || !hasDecided) return;

    let lastDismissISO: string | null = null;
    let dismissedThisSession = false;
    try {
      lastDismissISO = localStorage.getItem(DISMISS_KEY);
      dismissedThisSession = sessionStorage.getItem(SESSION_KEY) === '1';
    } catch {
      /* storage non disponibile (private mode) — tratta come mai chiuso */
    }

    setOpen(
      shouldShowBanner({
        enabled: banner.enabled,
        imageUrl: banner.imageUrl,
        isAuthenticated,
        reshowDays: banner.reshowDays,
        todayISO: todayISO(),
        lastDismissISO,
        dismissedThisSession,
      }),
    );
  }, [isLoading, isAuthenticated, banner, hasDecided]);

  function dismiss() {
    try {
      if (banner?.reshowDays === 0) {
        sessionStorage.setItem(SESSION_KEY, '1');
      } else {
        localStorage.setItem(DISMISS_KEY, todayISO());
      }
    } catch {
      /* ignora */
    }
    setOpen(false);
  }

  function handleOpenChange(next: boolean) {
    if (!next) dismiss();
  }

  function handleImageClick() {
    dismiss();
    navigate('/register');
  }

  if (!banner?.imageUrl) return null;

  return (
    <Modal open={open} onOpenChange={handleOpenChange}>
      <ModalPortal>
        <ModalOverlay className="bg-[#231F55]/30 backdrop-blur-lg" />
        <DialogPrimitive.Content className="fixed left-[50%] top-[50%] z-50 w-[calc(100%-2rem)] max-w-[760px] translate-x-[-50%] translate-y-[-50%] overflow-hidden rounded-2xl shadow-2xl focus:outline-none">
          <ModalTitle className="sr-only">Benvenuto su Klaro7</ModalTitle>
          <ModalDescription className="sr-only">
            Iscriviti ora e attiva il tuo bonus di benvenuto.
          </ModalDescription>

          <ModalClose className="absolute right-3 top-3 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-black/30 text-white/80 transition-colors hover:bg-black/50 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/40">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
            <span className="sr-only">Chiudi</span>
          </ModalClose>

          <button type="button" onClick={handleImageClick} className="block w-full focus:outline-none">
            <img src={banner.imageUrl} alt="Klaro7 — iscriviti ora" className="block w-full" />
          </button>
        </DialogPrimitive.Content>
      </ModalPortal>
    </Modal>
  );
}
